// Lost Deal Analysis tab — SGNL-216 rewrite.
//
// Replaces the SGNL-214 rollup surface (which filtered on current
// probability=0 for Closed Lost rows, so the page showed zero data).
// Now driven by the historical-stage data the daily pipeline computes
// from opportunity_history (see services/lost_aggregates.py):
//
//   pre_close_max_stage       — peak stage the opp reached (e.g.
//                                "Final Negotiation"). Closed Lost
//                                rows still have current stage =
//                                "Closed Lost", but THIS field
//                                carries the meaningful pre-close state.
//   pre_close_max_probability — peak SF probability ever (0-100)
//   peak_amount               — highest amount the deal carried
//
// The tab uses the GLOBAL filter bar (BU / Owner / Stage / AI Maturity /
// Date / Search) — no duplicated filter UI on the tab. The probability
// dropdown stays as a per-tab control because the threshold semantic
// is specific to lost-deal analysis.
//
// Per-deal "Analyse" click navigates to the existing #opp/{id} deal
// detail page (which already surfaces timeline + rollup + sentiment).
// Multi-deal aggregate analysis was added in SGNL-219 (static charts)
// + SGNL-220 (LLM cross-deal coaching) — see renderMultiDealAnalysisPanel.

import { fetchLostCoachingBatch } from "../api";
import {
  computeLastActivityGap,
  computeLostByPreCloseStage,
  computeTopLossReasons,
  filterQualifiedLost,
  type LostActivityGapBucket,
  type LostByPreCloseStageRow,
  type LostReasonRow,
} from "../lib/aggregations";
import { downloadCsv, toCsvRow } from "../lib/csv";
import { esc, fmtMoney } from "../lib/format";
import type { MultiDealCoachingOut, OpportunityRow } from "../types";

// ---------------------------------------------------------------------------
// Probability preset — semantic shift from SGNL-214: now filters on
// `pre_close_max_probability` (HISTORICAL peak) instead of the row's
// `probability` field (which is always 0 for Closed Lost). Operators
// can ask "show me deals that reached ≥30% before losing" and get
// the right answer instead of an empty page.
// ---------------------------------------------------------------------------

interface ProbabilityPreset {
  value: string;
  label: string;
  min: number | null;
}

const PROBABILITY_PRESETS: ReadonlyArray<ProbabilityPreset> = Object.freeze([
  { value: "all", label: "All probabilities", min: null },
  { value: "q5",  label: "Reached ≥5% (qualified+)",  min: 5 },
  { value: "m30", label: "Reached ≥30% (mid-stage+)", min: 30 },
  { value: "l70", label: "Reached ≥70% (late-stage+)", min: 70 },
]);
const DEFAULT_PROBABILITY_PRESET = "all"; // SGNL-216: changed from "q5" — the
// historical-stage filter is now meaningful and we want EVERY closed-lost
// row visible by default. Operator narrows via dropdown when they want.
const SESSION_KEY = "signal:lost-deal-analysis-probability-preset";

function isValidPreset(v: unknown): v is string {
  return typeof v === "string" && PROBABILITY_PRESETS.some((p) => p.value === v);
}

function readPersistedPreset(): string {
  try {
    const v = sessionStorage.getItem(SESSION_KEY);
    if (isValidPreset(v)) return v;
  } catch {
    // Safari Private Browsing pre-iOS 17 throws — silent fallback.
  }
  return DEFAULT_PROBABILITY_PRESET;
}

let _probabilityPreset: string = readPersistedPreset();

export function setLostProbabilityPreset(value: string): void {
  if (!isValidPreset(value)) return;
  _probabilityPreset = value;
  try {
    sessionStorage.setItem(SESSION_KEY, value);
  } catch {
    // same Safari guard
  }
}

export function getLostProbabilityPreset(): string {
  return _probabilityPreset;
}

function getProbabilityMin(): number | null {
  return PROBABILITY_PRESETS.find((p) => p.value === _probabilityPreset)?.min ?? null;
}

// ---------------------------------------------------------------------------
// Selection state — module-level Set of opp ids currently checked.
// Persists across re-renders but resets on tab swap.
// ---------------------------------------------------------------------------

let _selection: Set<string> = new Set();

/** Test-only seam — reset module state between cases. */
export function _resetLostModuleState(): void {
  _probabilityPreset = DEFAULT_PROBABILITY_PRESET;
  _selection = new Set();
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Filter — uses the new HISTORICAL pre_close_max_probability field.
// Falls back to the row's current probability when the aggregate is
// NULL (opps with no history data — ~2.5% of the corpus).
// ---------------------------------------------------------------------------

function preCloseProbability(r: OpportunityRow): number | null {
  if (typeof r.pre_close_max_probability === "number" && Number.isFinite(r.pre_close_max_probability)) {
    return r.pre_close_max_probability;
  }
  if (typeof r.probability === "number" && Number.isFinite(r.probability)) {
    return r.probability;
  }
  return null;
}

function lostFromStage(r: OpportunityRow): string {
  return (r.pre_close_max_stage ?? "").trim() || "(no history)";
}

function lostDeals(rows: OpportunityRow[]): OpportunityRow[] {
  // isLost is internal to filterQualifiedLost; we call it with min=null
  // to get the full closed-lost subset, then apply the historical filter.
  //
  // Filter predicate uses the HISTORICAL aggregate strictly — no fallback
  // to `r.probability`. CL rows have probability=0 in SF, so a fallback
  // would silently mark every NULL-aggregate row as "below threshold"
  // for any non-null min, producing the same broken-empty-page behaviour
  // we just fixed. ~2.5% of CL opps have no history rows and thus no
  // aggregate — those rows disappear from threshold-filtered views,
  // which is honest ("we have no evidence this deal ever reached X%").
  // They remain visible under the "All" preset (default).
  // The render path keeps the `r.probability` fallback (preCloseProbability)
  // so the table can show *something* in the Lost-at column rather than "—".
  return filterQualifiedLost(rows, null).filter((r) => {
    const min = getProbabilityMin();
    if (min === null) return true;
    const p = r.pre_close_max_probability;
    return typeof p === "number" && Number.isFinite(p) && p >= min;
  });
}

// ---------------------------------------------------------------------------
// CSV — full-set export, RFC-4180 quoting via lib/csv.
// ---------------------------------------------------------------------------

/** Exported for testing — pure CSV builder so tests don't need to
 *  intercept Blob construction. */
export function toLostAnalysisCsv(rows: readonly OpportunityRow[]): string {
  const header = toCsvRow([
    "Opp", "Account", "BU", "Owner", "Lost from stage", "Lost at probability",
    "Peak $", "Close date", "Last activity", "Reason",
  ]);
  const body = rows.map((r) =>
    toCsvRow([
      r.name ?? "",
      r.account_name ?? "",
      r.bu ?? "",
      r.owner_name ?? "",
      r.pre_close_max_stage ?? "",
      typeof r.pre_close_max_probability === "number" && Number.isFinite(r.pre_close_max_probability)
        ? r.pre_close_max_probability
        : "",
      typeof r.peak_amount === "number" && Number.isFinite(r.peak_amount)
        ? r.peak_amount
        : (typeof r.amount === "number" && Number.isFinite(r.amount) ? r.amount : ""),
      r.close_date ?? "",
      r.last_activity_date ?? "",
      (r.reason_no_ai ?? "").trim() || "(unknown)",
    ]),
  );
  return [header, ...body].join("\r\n") + "\r\n";
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

function renderProbabilityFilterBar(currentValue: string): string {
  return `
    <div class="progress-probability-filter" data-testid="lost-probability-filter">
      <label class="progress-probability-label">
        Reached probability
        <select data-testid="lost-probability-select"
                aria-label="Filter to deals that reached at least this probability before losing">
          ${PROBABILITY_PRESETS.map(
            (p) =>
              `<option value="${esc(p.value)}" ${currentValue === p.value ? "selected" : ""}>${esc(p.label)}</option>`,
          ).join("")}
        </select>
      </label>
      <p class="muted progress-probability-help">
        Filters on the peak probability each deal hit before closing
        (from opportunity_history). The deal's CURRENT probability is
        always 0% on Closed Lost — useless as a filter.
      </p>
    </div>`;
}

function renderTableRow(r: OpportunityRow, isSelected: boolean): string {
  const lostStage = lostFromStage(r);
  const lostProb = preCloseProbability(r);
  const peakAmt = (typeof r.peak_amount === "number" && Number.isFinite(r.peak_amount))
    ? r.peak_amount
    : (typeof r.amount === "number" && Number.isFinite(r.amount) ? r.amount : null);
  const reason = (r.reason_no_ai ?? "").trim() || "—";
  // OpportunityRow.name is typed non-null, but `esc()` returns "" for
  // null/undefined which would render an invisible link. Apply a visible
  // fallback for the (extremely rare) malformed-row case (PR-Agent #135 imp).
  const displayName = (r.name ?? "").trim() || "(unnamed deal)";
  return `
    <tr data-testid="lost-deal-row" data-opp-id="${esc(r.id)}">
      <td class="lost-checkbox-cell">
        <input type="checkbox"
               data-action="lost-select"
               data-opp-id="${esc(r.id)}"
               ${isSelected ? "checked" : ""}
               aria-label="Select ${esc(displayName)}" />
      </td>
      <td>
        <a href="#opp/${encodeURIComponent(r.id)}"
           data-action="lost-open-detail"
           data-opp-id="${esc(r.id)}">${esc(displayName)}</a>
      </td>
      <td>${esc(r.account_name ?? "—")}</td>
      <td>${esc(r.bu ?? "—")}</td>
      <td>${esc(r.owner_name ?? "—")}</td>
      <td>${esc(lostStage)}</td>
      <td class="num">${lostProb !== null ? esc(`${lostProb.toFixed(0)}%`) : "—"}</td>
      <td class="num">${peakAmt !== null ? fmtMoney(peakAmt) : "—"}</td>
      <td>${esc(r.close_date ?? "—")}</td>
      <td>${esc(reason)}</td>
    </tr>`;
}

// ---------------------------------------------------------------------------
// SGNL-219 — multi-deal aggregate analysis panel
//
// Renders when the operator selects ≥ 2 lost deals + clicks Analyse.
// Bundle-only — no per-opp /api/opportunities/{id} calls. Three charts:
//   1. Lost-from-stage histogram (count + $ per pre_close_max_stage)
//   2. Last-activity-gap distribution (0–7d / 8–30d / 31–90d / >90d / unknown)
//   3. Top loss reasons (count + $ per reason_no_ai)
//
// Helpers (`computeLostByPreCloseStage`, `computeLastActivityGap`,
// `computeTopLossReasons`) live in lib/aggregations.ts so the same
// computations are testable in isolation + reusable by any future
// surface (e.g. a v2 that adds time-per-stage averages).
//
// Charts here are pure HTML bar charts (no Chart.js dependency). Bars
// are CSS-width-percent against the max-count or max-amount in the
// bucket set. Keeps the bundle small + works without a canvas context.
// ---------------------------------------------------------------------------

/** Exported for testing — pure renderer so tests don't need to mount
 *  the full Lost tab to assert on the panel's output. */
export function renderMultiDealAnalysisPanel(selected: readonly OpportunityRow[]): string {
  if (selected.length < 2) return "";

  const stageRows = computeLostByPreCloseStage(selected);
  // computeLastActivityGap + computeTopLossReasons accept a `min` cap;
  // pass null because the operator-side filtering already happened upstream
  // (the selection IS the cohort, no further probability filtering needed).
  const gapBuckets = computeLastActivityGap(selected, null);
  const reasonRows = computeTopLossReasons(selected, null);

  const totalCount = selected.length;
  const totalAmount = selected.reduce((s, r) => {
    // Mirrors `computeLostByPreCloseStage`: peak_amount preferred,
    // fall back to amount, negatives dropped (SF data-bug artefact).
    // Pre-PR audit + PR-Agent #138 imp 6 — without the `>= 0` guard,
    // the header total could go negative while the stage histogram
    // (which DOES drop negatives) shows positive bucket sums.
    const v =
      typeof r.peak_amount === "number" && Number.isFinite(r.peak_amount) && r.peak_amount >= 0
        ? r.peak_amount
        : typeof r.amount === "number" && Number.isFinite(r.amount) && r.amount >= 0
          ? r.amount
          : 0;
    return s + v;
  }, 0);

  return `
    <section
      class="lost-multi-panel"
      role="region"
      aria-label="Multi-deal aggregate analysis"
      data-testid="lost-multi-deal-panel"
    >
      <header class="lost-multi-panel-header">
        <h3 class="lost-multi-panel-title">
          Aggregate analysis · ${totalCount.toLocaleString()} deals · ${fmtMoney(totalAmount)}
        </h3>
        <button type="button"
                class="btn-secondary lost-multi-panel-clear"
                data-action="lost-multi-clear"
                data-testid="lost-multi-clear">
          Clear selection
        </button>
      </header>
      <!-- SGNL-220 — LLM cross-deal coaching block. Hydrated by
           hydrateMultiDealCoaching() after the static charts render.
           Generation-stamped so a stale fetch from a previous Analyse
           click can't overwrite a fresher render. -->
      <div data-testid="lost-multi-coaching-slot" data-coaching-generation="">
        ${renderMultiDealCoachingLoading()}
      </div>
      <div class="lost-multi-panel-charts">
        ${renderStageHistogram(stageRows)}
        ${renderActivityGapHistogram(gapBuckets)}
        ${renderTopReasonsList(reasonRows)}
      </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// SGNL-220 — multi-deal LLM coaching block (lives at the top of the panel)
// ---------------------------------------------------------------------------

function renderMultiDealCoachingLoading(): string {
  return `
    <div class="lost-multi-coaching lost-multi-coaching--loading"
         data-testid="lost-multi-coaching-loading">
      <h4 class="lost-multi-coaching-title">Cross-deal coaching</h4>
      <p class="muted">Synthesising pattern across selected deals…</p>
    </div>`;
}

function renderMultiDealCoachingEmpty(): string {
  return `
    <div class="lost-multi-coaching lost-multi-coaching--empty"
         data-testid="lost-multi-coaching-empty">
      <h4 class="lost-multi-coaching-title">Cross-deal coaching</h4>
      <p class="muted">
        No per-deal coaching is cached yet for the selected opps. The
        daily ingest backfills up to 50 deals per night — a large
        corpus may take several nights to converge. To get coaching
        immediately, open a single deal: clicking its row opens the
        detail page and generates coaching on-demand.
      </p>
    </div>`;
}

function renderMultiDealCoachingError(msg: string): string {
  return `
    <div class="lost-multi-coaching lost-multi-coaching--error"
         data-testid="lost-multi-coaching-error">
      <h4 class="lost-multi-coaching-title">Cross-deal coaching</h4>
      <p class="error">Couldn't synthesise: ${esc(msg)}</p>
    </div>`;
}

function renderMultiDealCoachingPopulated(c: MultiDealCoachingOut): string {
  const patterns =
    c.common_patterns.length === 0
      ? ""
      : `<div class="lost-multi-coaching-section">
           <h5 class="lost-multi-coaching-section-title">Common patterns</h5>
           <ul class="lost-multi-coaching-list">
             ${c.common_patterns.map((p) => `<li>${esc(p)}</li>`).join("")}
           </ul>
         </div>`;
  const actions =
    c.recommended_actions.length === 0
      ? ""
      : `<div class="lost-multi-coaching-section">
           <h5 class="lost-multi-coaching-section-title">Cohort-level actions</h5>
           <ul class="lost-multi-coaching-list lost-multi-coaching-list--actions">
             ${c.recommended_actions.map((a) => `<li>${esc(a)}</li>`).join("")}
           </ul>
         </div>`;
  return `
    <div class="lost-multi-coaching lost-multi-coaching--populated"
         data-testid="lost-multi-coaching">
      <h4 class="lost-multi-coaching-title">Cross-deal coaching</h4>
      ${c.summary ? `<p class="lost-multi-coaching-summary">${esc(c.summary)}</p>` : ""}
      ${patterns}
      ${actions}
      <p class="muted lost-multi-coaching-provenance">
        AI-generated across ${c.opportunity_ids.length.toLocaleString()} deals · ${esc(c.model)} · prompt ${esc(c.prompt_version)}
      </p>
    </div>`;
}

async function hydrateMultiDealCoaching(
  host: HTMLElement,
  oppIds: readonly string[],
  generation: string,
): Promise<void> {
  const slot = host.querySelector<HTMLElement>('[data-testid="lost-multi-coaching-slot"]');
  if (!slot) return;
  try {
    const coaching = await fetchLostCoachingBatch(oppIds);
    if (slot.dataset.coachingGeneration !== generation) return;
    if (coaching === null) {
      slot.innerHTML = renderMultiDealCoachingEmpty();
    } else {
      slot.innerHTML = renderMultiDealCoachingPopulated(coaching);
    }
  } catch (err) {
    if (slot.dataset.coachingGeneration !== generation) return;
    slot.innerHTML = renderMultiDealCoachingError(
      err instanceof Error ? err.message : String(err),
    );
  }
}

// Pure-HTML bars (CSS width-percent) are intentional here; see
// `ai_breakdown.ts` for the Chart.js canvas pattern used on other tabs.
// Bars are bundle-size-cheap, screen-reader-friendly (no <canvas>), and
// snapshot-testable. Pre-PR architect NIT — locks the choice for any
// future contributor.

/** Bar-chart row used by all three histograms in the panel. `testid` is
 *  a CALLER-CONTROLLED-LITERAL ONLY — sub-renderers pass hardcoded
 *  testid strings (lost-multi-stage-bar / lost-multi-activity-bar /
 *  lost-multi-reasons-bar). The parameter is escaped at the attribute
 *  boundary as defence-in-depth (pre-PR security LOW). */
function renderBarRow(
  label: string,
  count: number,
  amount: number,
  maxCount: number,
  testid: string,
): string {
  // Sanitize inputs before ANY arithmetic — `Math.min(100, Math.max(0, NaN))`
  // is still NaN, and `NaN.toLocaleString()` is "NaN", which would render
  // `width:NaN%` + "NaN deals". Pre-PR PR-Agent #138 imp 3. Mirrors the
  // negative-amount guard in `computeLostByPreCloseStage`.
  const safeCount = Number.isFinite(count) && count >= 0 ? Math.trunc(count) : 0;
  const safeMax = Number.isFinite(maxCount) && maxCount >= 0 ? Math.trunc(maxCount) : 0;
  const safeAmount = Number.isFinite(amount) && amount >= 0 ? amount : 0;
  // Width-by-count; max count in any row = 100%. Falls back to 0% when
  // the bucket set is all-zero (degenerate empty cohort). The clamp
  // is belt-and-suspenders on top of the sanitization above.
  const raw = safeMax > 0 ? Math.round((safeCount / safeMax) * 100) : 0;
  const pct = Math.min(100, Math.max(0, raw));
  const aria = `${label}: ${safeCount.toLocaleString()} deals (${fmtMoney(safeAmount)})`;
  return `
    <li class="lost-multi-bar-row" data-testid="${esc(testid)}" data-label="${esc(label)}">
      <div class="lost-multi-bar-label">${esc(label)}</div>
      <div class="lost-multi-bar-wrap" role="img" aria-label="${esc(aria)}">
        <div class="lost-multi-bar" style="width:${pct}%;" aria-hidden="true"></div>
      </div>
      <div class="lost-multi-bar-stats">
        <span class="lost-multi-bar-count">${safeCount.toLocaleString()}</span>
        <span class="lost-multi-bar-amount muted">${fmtMoney(safeAmount)}</span>
      </div>
    </li>`;
}

function renderStageHistogram(rows: readonly LostByPreCloseStageRow[]): string {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  const body = rows.length === 0
    ? `<p class="empty-bucket">No stage data in the selection.</p>`
    : `<ul class="lost-multi-bar-list" data-testid="lost-multi-stage-list">
        ${rows.map((r) => renderBarRow(r.stage, r.count, r.amount, max, "lost-multi-stage-bar")).join("")}
      </ul>`;
  return `
    <article class="lost-multi-chart" data-testid="lost-multi-stage-chart">
      <h4 class="lost-multi-chart-title">Lost from stage</h4>
      ${body}
    </article>`;
}

function renderActivityGapHistogram(buckets: readonly LostActivityGapBucket[]): string {
  // `max === 0` covers the empty-cohort case (no buckets had any rows).
  // No separate totalCount needed — pre-PR code-reviewer NIT.
  const max = buckets.reduce((m, b) => Math.max(m, b.count), 0);
  const body = max === 0
    ? `<p class="empty-bucket">No activity-gap data in the selection.</p>`
    : `<ul class="lost-multi-bar-list" data-testid="lost-multi-activity-list">
        ${buckets.map((b) => renderBarRow(b.label, b.count, b.amount, max, "lost-multi-activity-bar")).join("")}
      </ul>
      <p class="muted lost-multi-chart-help">
        Days between last conversation and the close-lost date. Long gap →
        neglect; short gap → competitive loss.
      </p>`;
  return `
    <article class="lost-multi-chart" data-testid="lost-multi-activity-chart">
      <h4 class="lost-multi-chart-title">Last activity gap</h4>
      ${body}
    </article>`;
}

function renderTopReasonsList(rows: readonly LostReasonRow[]): string {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  const body = rows.length === 0
    ? `<p class="empty-bucket">No loss reasons recorded on the selection.</p>`
    : `<ul class="lost-multi-bar-list" data-testid="lost-multi-reasons-list">
        ${rows.map((r) => renderBarRow(r.reason, r.count, r.amount, max, "lost-multi-reasons-bar")).join("")}
      </ul>`;
  return `
    <article class="lost-multi-chart" data-testid="lost-multi-reasons-chart">
      <h4 class="lost-multi-chart-title">Top loss reasons</h4>
      ${body}
    </article>`;
}

export function renderLostTab(host: HTMLElement, rows: OpportunityRow[]): void {
  const lost = lostDeals(rows);
  // Sort by peak amount desc — biggest losses first (matches operator intent).
  const sorted = [...lost].sort((a, b) => {
    const aAmt = (typeof a.peak_amount === "number" && Number.isFinite(a.peak_amount)) ? a.peak_amount : (a.amount ?? 0);
    const bAmt = (typeof b.peak_amount === "number" && Number.isFinite(b.peak_amount)) ? b.peak_amount : (b.amount ?? 0);
    return bAmt - aAmt;
  });
  // Prune the selection — any id no longer in the lost set (e.g. user
  // changed global filters) must drop out, otherwise the analyse-count
  // diverges from the visible-checked count.
  const visibleIds = new Set(sorted.map((r) => r.id));
  for (const id of _selection) {
    if (!visibleIds.has(id)) _selection.delete(id);
  }

  const totalCount = sorted.length;
  const totalAmount = sorted.reduce((s, r) => {
    const v = (typeof r.peak_amount === "number" && Number.isFinite(r.peak_amount))
      ? r.peak_amount
      : (typeof r.amount === "number" && Number.isFinite(r.amount) ? r.amount : 0);
    return s + v;
  }, 0);
  const selectedCount = _selection.size;
  const ROW_CAP = 500;
  const capped = sorted.slice(0, ROW_CAP);

  host.innerHTML = `
    <div class="tab-content" data-testid="lost-tab">
      <div class="tab-header" style="justify-content:space-between;">
        <h2>
          Lost Deal Analysis
          <span class="row-count" data-testid="lost-row-count">
            ${totalCount.toLocaleString()} deals · ${fmtMoney(totalAmount)}
          </span>
        </h2>
        <span style="display:flex;gap:8px;align-items:center;">
          <button type="button" class="btn-secondary"
                  data-action="lost-analyse"
                  data-testid="lost-analyse-btn"
                  ${selectedCount === 0 ? "disabled" : ""}
                  title="${esc(selectedCount === 0 ? "Select one or more deals to analyse" : `Analyse ${selectedCount} selected`)}">
            Analyse ${selectedCount > 0 ? selectedCount : ""} selected
          </button>
          <button type="button" class="btn-secondary"
                  data-action="lost-csv"
                  data-testid="lost-csv-export"
                  ${totalCount === 0 ? "disabled" : ""}>
            Export CSV
          </button>
        </span>
      </div>
      ${renderProbabilityFilterBar(getLostProbabilityPreset())}
      <div class="lost-banner-host" data-testid="lost-banner-host"></div>
      ${totalCount === 0
        ? `<p class="empty-bucket">No lost deals match the current filters. Widen the global filters above or switch the probability dropdown to "All".</p>`
        : `<div class="table-wrap table-wrap--scrollable">
          <table class="data-table" data-testid="lost-table">
            <thead>
              <tr>
                <th class="lost-checkbox-cell">
                  <input type="checkbox"
                         data-action="lost-select-all"
                         data-testid="lost-select-all"
                         ${selectedCount > 0 && selectedCount === capped.length ? "checked" : ""}
                         aria-label="${
                           sorted.length > ROW_CAP
                             ? `Select all ${ROW_CAP} visible deals (${(sorted.length - ROW_CAP).toLocaleString()} more hidden by row cap)`
                             : "Select all visible deals"
                         }" />
                </th>
                <th>Opp</th>
                <th>Account</th>
                <th>BU</th>
                <th>Owner</th>
                <th>Lost from stage</th>
                <th class="num">Lost at</th>
                <th class="num">Peak $</th>
                <th>Closed</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              ${capped.map((r) => renderTableRow(r, _selection.has(r.id))).join("")}
            </tbody>
          </table>
          ${sorted.length > ROW_CAP
            ? `<p class="table-footnote">Showing first ${ROW_CAP} of ${sorted.length.toLocaleString()} deals by peak amount. Narrow the filters above to see more.</p>`
            : ""}
        </div>`}
    </div>`;

  // Probability dropdown
  const select = host.querySelector<HTMLSelectElement>('[data-testid="lost-probability-select"]');
  select?.addEventListener("change", () => {
    setLostProbabilityPreset(select.value);
    renderLostTab(host, rows);
  });

  // Per-row checkbox
  for (const cb of host.querySelectorAll<HTMLInputElement>('[data-action="lost-select"]')) {
    cb.addEventListener("change", () => {
      const id = cb.dataset.oppId;
      if (!id) return;
      if (cb.checked) _selection.add(id);
      else _selection.delete(id);
      // Re-render just the analyse button + count (keep checkboxes from
      // flickering). Cheap re-render is fine at <500 rows.
      renderLostTab(host, rows);
    });
  }

  // Select-all — when the result set is capped (>ROW_CAP rows), the
  // checkbox renders with `indeterminate=true` so the operator sees
  // immediately that "all visible" ≠ "all matching". HTML doesn't
  // support `indeterminate` as a string attribute; must be set on the
  // DOM property post-render. Code-reviewer pre-PR HIGH finding.
  const selectAllEl = host.querySelector<HTMLInputElement>('[data-testid="lost-select-all"]');
  if (selectAllEl && sorted.length > ROW_CAP && selectedCount === capped.length && selectedCount > 0) {
    selectAllEl.indeterminate = true;
  }
  selectAllEl?.addEventListener("change", (e) => {
    const checked = (e.target as HTMLInputElement).checked;
    if (checked) {
      _selection = new Set(capped.map((r) => r.id));
    } else {
      _selection = new Set();
    }
    renderLostTab(host, rows);
  });

  // Analyse — for N=1, navigate to the existing deal detail page.
  // For N>=2, render the SGNL-219 multi-deal aggregate analysis panel
  // inline in the banner-host slot.
  host.querySelector<HTMLButtonElement>('[data-action="lost-analyse"]')?.addEventListener("click", () => {
    const ids = [..._selection];
    if (ids.length === 0) return;
    if (ids.length === 1) {
      window.location.hash = `#opp/${encodeURIComponent(ids[0])}`;
      return;
    }
    const banner = host.querySelector<HTMLElement>('[data-testid="lost-banner-host"]');
    if (!banner) return;
    const selectedRows = sorted.filter((r) => _selection.has(r.id));
    banner.innerHTML = renderMultiDealAnalysisPanel(selectedRows);
    // Bind the clear-selection action AFTER the panel mounts — the
    // button doesn't exist until renderMultiDealAnalysisPanel writes
    // it into the banner-host. banner.innerHTML wipes any previous
    // panel (and its listener) on re-Analyse, so no double-bind
    // accumulation across clicks (pre-PR code-reviewer LOW).
    //
    // Tech debt — this whole file's post-innerHTML re-bind pattern
    // (per-row checkbox / Analyse / clear-selection) is a smell. Event
    // delegation on `host` would eliminate it but introducing the
    // third pattern in THIS PR would be worse than the existing
    // approach. Pre-PR architect MED — defer to a focused refactor.
    const clearBtn = banner.querySelector<HTMLButtonElement>(
      '[data-action="lost-multi-clear"]',
    );
    if (clearBtn) {
      clearBtn.addEventListener("click", () => {
        _selection = new Set();
        renderLostTab(host, rows);
      });
    }
    // SGNL-220 — kick off the cross-deal LLM coaching synthesis in
    // parallel with the static charts already on screen. Same
    // generation-stamp race-guard as the opp-detail card hydrations
    // so a stale fetch from a previous Analyse click can't overwrite
    // a fresher render.
    const coachingSlot = banner.querySelector<HTMLElement>(
      '[data-testid="lost-multi-coaching-slot"]',
    );
    if (coachingSlot) {
      const generation = String(Date.now());
      coachingSlot.dataset.coachingGeneration = generation;
      void hydrateMultiDealCoaching(
        banner,
        selectedRows.map((r) => r.id),
        generation,
      );
    }
    // Scroll the panel into view so the operator sees the result of
    // their action without hunting for it below the fold.
    banner.querySelector('[data-testid="lost-multi-deal-panel"]')?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  });

  // CSV export — uses the FILTERED set (post-global-filter, post-
  // probability-dropdown), not just the selection. Operators expect
  // "export what I'm looking at."
  host.querySelector<HTMLButtonElement>('[data-action="lost-csv"]')?.addEventListener("click", () => {
    if (sorted.length === 0) return;
    downloadCsv(
      `signal-lost-deal-analysis-${new Date().toISOString().slice(0, 10)}.csv`,
      toLostAnalysisCsv(sorted),
    );
  });

  // BU drill-through is intentionally not added here — the global
  // filter bar above already handles BU selection cleanly. Keeping
  // the tab table click-through-free avoids the "click a row to
  // navigate vs click a row to select" UX conflict.
}
